'use client'

import { useEffect, useCallback } from 'react'
import { OnboardingSpotlight } from './OnboardingSpotlight'
import { OnboardingTooltip } from './OnboardingTooltip'
import { ConfettiAnimation } from './ConfettiAnimation'
import type { OnboardingStep, OnboardingStepId } from '@/types/onboarding'
import type { WorkflowStep } from '@/types/synoptique'

interface OnboardingOverlayProps {
  isActive: boolean
  currentStep: OnboardingStep | null
  currentIndex: number
  totalSteps: number
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
  onStepChange?: (step: WorkflowStep) => void
  currentWorkflowStep?: WorkflowStep
  cameFromBack?: boolean
  isDragging?: boolean
  draggingType?: 'processor' | 'cabinet' | null
}

export function OnboardingOverlay({
  isActive,
  currentStep,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onStepChange,
  currentWorkflowStep,
  cameFromBack = false,
  isDragging = false,
  draggingType = null,
}: OnboardingOverlayProps) {

  // Handle keyboard navigation
  useEffect(() => {
    if (!isActive) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onSkip()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, onSkip])

  // Auto-navigate to correct workflow step when needed
  useEffect(() => {
    if (!isActive || !currentStep || !onStepChange) return

    if (currentStep.workflowStep && currentStep.workflowStep !== currentWorkflowStep) {
      // Delay the step change slightly to allow smooth transition
      const timer = setTimeout(() => {
        onStepChange(currentStep.workflowStep!)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isActive, currentStep, currentWorkflowStep, onStepChange])

  if (!isActive || !currentStep) return null

  const isFirst = currentIndex === 0
  const isLast = currentIndex === totalSteps - 1

  // Determine if we should show workspace highlight during drag
  const shouldShowDragHighlight = isDragging && draggingType && 
    (currentStep.id === 'step2-select-and-drag-processor' || currentStep.id === 'step3-select-and-drag-cabinet')

  return (
    <>
      {/* Workspace highlight when dragging processor/cabinet */}
      {shouldShowDragHighlight && (
        <OnboardingSpotlight
          target='[data-onboarding="workspace"]'
          padding={16}
          isVisible={true}
          allowInteraction={true}
          disableOverlay={false}
        />
      )}

      {/* Spotlight overlay (normal) */}
      {!shouldShowDragHighlight && currentStep.showSpotlight && currentStep.target && (
        <OnboardingSpotlight
          target={currentStep.target}
          padding={currentStep.spotlightPadding}
          isVisible={true}
          allowInteraction={currentStep.allowInteraction}
          disableOverlay={currentStep.disableOverlay}
        />
      )}

      {/* Dark overlay when no spotlight (unless disableOverlay or dragging) */}
      {!shouldShowDragHighlight && !currentStep.showSpotlight && !currentStep.disableOverlay && (
        <div 
          className="fixed inset-0 bg-black/60 z-[9998]"
          style={{ pointerEvents: currentStep.allowInteraction ? 'none' : 'auto' }}
        />
      )}

      {/* Tooltip */}
      <OnboardingTooltip
        step={currentStep}
        onNext={onNext}
        onPrev={onPrev}
        onSkip={onSkip}
        currentIndex={currentIndex}
        totalSteps={totalSteps}
        isFirst={isFirst}
        isLast={isLast}
        cameFromBack={cameFromBack}
        isDragging={!!shouldShowDragHighlight}
      />

      {/* Confetti animation for completed step */}
      <ConfettiAnimation isActive={currentStep.id === 'completed'} />
    </>
  )
}
